// src/context/PermissionContext.js
//
// Phase 0B frontend permission context. Loads the current user's effective
// permission set once per login from GET /api/auth/me/permissions and
// exposes `can`/`canAny` for route guarding, sidebar visibility, and
// action-button gating.
//
// IMPORTANT: this is a UX convenience only. Every check here mirrors a
// check the backend (middlewares/authorize.js) already performs
// authoritatively — hiding a button here never substitutes for the
// server-side check. If this context fails to load (network error, etc.),
// it fails CLOSED (no permissions known -> `can()` returns false
// everywhere) rather than silently granting access.
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../api";
import { AuthContext } from "./AuthContext";

export const PermissionContext = createContext({
  permissions: [],
  loading: true,
  can: () => false,
  canAny: () => false,
  // TEMPORARY historical-task-entry feature flag (Phase 37) — fails closed
  // (false) the same way `permissions` does on a load error, so a broken
  // fetch never accidentally reveals the feature.
  featureFlags: {},
});

export const PermissionProvider = ({ children }) => {
  const { token, user } = useContext(AuthContext);
  const [permissions, setPermissions] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);
  // BUG FIX: this used to be keyed by `user.id` alone. That looks right for
  // "don't refetch on every unrelated re-render", but it silently also
  // means "the same user logging out and back in (e.g. right after their
  // role/permissions changed) never refetches", since user.id is identical
  // across that whole cycle. Root cause traced to
  // src/MainLayout/Header.js + src/components/AdminLayout/Header.js's
  // logout handlers, which only ever removed `"user"` from localStorage,
  // never `"token"` -- the still-valid JWT let AuthContext's own /auth/me
  // session-validation effect silently re-authenticate the SAME user id on
  // the next load, so this ref's "already loaded for user X" check kept
  // matching and a stale permission/feature-flag snapshot from before a
  // grant was ever fetched again. Both logout handlers are now fixed to
  // clear both keys, but this ref is ALSO switched to key off the actual
  // TOKEN string, not the user id -- a genuinely fresh login always issues
  // a brand-new JWT (jwt.sign() embeds `iat`, so two logins for the exact
  // same user never produce an identical token), so this is correct even
  // if some other future code path leaves a stale user id around. Session
  // identity, not user identity, is what "already loaded" must track.
  const loadedForSession = useRef(null);

  useEffect(() => {
    const resolvedUserId = user?.id;
    const willFetch = !!(token && resolvedUserId) && loadedForSession.current !== token;

    // TEMPORARY DIAGNOSTIC -- remove once the fresh-login refetch is
    // confirmed working end-to-end (see Header.js logout fix above).
    console.log("PERMISSION FETCH DEBUG", {
      user,
      resolvedUserId,
      loadedForSession: loadedForSession.current,
      willFetch,
    });

    if (!token || !resolvedUserId) {
      setPermissions([]);
      setFeatureFlags({});
      setLoading(false);
      loadedForSession.current = null;
      return;
    }

    if (loadedForSession.current === token) {
      return; // already loaded for this exact session/token
    }

    let cancelled = false;
    setLoading(true);
    // TEMPORARY DIAGNOSTIC -- remove alongside the one above.
    console.log("FETCHING /auth/me/permissions");

    api
      .get("/auth/me/permissions")
      .then((res) => {
        if (cancelled) return;
        setPermissions(Array.isArray(res.data?.permissions) ? res.data.permissions : []);
        setFeatureFlags(res.data?.featureFlags && typeof res.data.featureFlags === "object" ? res.data.featureFlags : {});
        loadedForSession.current = token;
      })
      .catch(() => {
        if (cancelled) return;
        setPermissions([]); // fail closed
        setFeatureFlags({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, user?.id]);

  const can = useCallback(
    (moduleKey, actionKey) =>
      permissions.some((p) => p.moduleKey === moduleKey && p.actionKey === actionKey && p.isAllowed),
    [permissions]
  );

  const canAny = useCallback(
    (permissionList) => permissionList.some(({ moduleKey, actionKey }) => can(moduleKey, actionKey)),
    [can]
  );

  return (
    <PermissionContext.Provider value={{ permissions, loading, can, canAny, featureFlags }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
